import { useEffect, useRef } from "react";
import { Send, ThumbsDown, ThumbsUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useChat } from "@/hooks/use-chat";

export function HomeChat() {
  const {
    messages,
    input,
    setInput,
    sending,
    feedback,
    sendFeedback,
    handleSubmit,
  } = useChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  return (
    <div className="mx-auto flex h-[28rem] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-5 py-4">
        <p className="font-display text-sm font-semibold">
          Preisvergleich-Assistent
        </p>
        <p className="text-xs text-muted-foreground">
          Frag direkt nach Elektronik, Handys oder Computern.
        </p>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-5 py-4"
      >
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "flex flex-col",
              m.role === "user" ? "items-end" : "items-start",
            )}
          >
            <div
              className={cn(
                "max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap",
                m.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground border border-border",
              )}
            >
              {m.text}
            </div>
            {m.role === "bot" && (
              <div className="mt-1 flex gap-1 pl-1">
                <button
                  type="button"
                  onClick={() => sendFeedback(m.id, "up", m.text)}
                  aria-label="Antwort hilfreich"
                  aria-pressed={feedback[m.id] === "up"}
                  className={cn(
                    "rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
                    feedback[m.id] === "up" && "text-primary",
                  )}
                >
                  <ThumbsUp
                    className="h-3.5 w-3.5"
                    fill={feedback[m.id] === "up" ? "currentColor" : "none"}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => sendFeedback(m.id, "down", m.text)}
                  aria-label="Antwort nicht hilfreich"
                  aria-pressed={feedback[m.id] === "down"}
                  className={cn(
                    "rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
                    feedback[m.id] === "down" && "text-primary",
                  )}
                >
                  <ThumbsDown
                    className="h-3.5 w-3.5"
                    fill={feedback[m.id] === "down" ? "currentColor" : "none"}
                  />
                </button>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              …
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 border-t border-border p-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Nachricht schreiben…"
          aria-label="Nachricht"
          disabled={sending}
          className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
        <button
          type="submit"
          aria-label="Senden"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          disabled={!input.trim() || sending}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
